/**
 * JobDescriptionDialog
 *
 * Generates a grounded, editable job description for an extracted contract role,
 * then lets the user copy it as board-safe plain text or export .md / .docx.
 * Every field is editable before export (human-in-the-loop).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { X, Loader2, Copy, FileDown, FileText, RefreshCw, ShieldCheck, AlertTriangle, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  buildJobDescriptionPayload,
  generateJobDescription,
  jobDescriptionToPlainText,
  exportJobDescriptionMarkdown,
  exportJobDescriptionWord,
  profileVerbatimFields,
} from '../../services/JobDescriptionService';
import { detectUnknownAcronyms } from '../../lib/export/jobDescriptionNormalize';
import type { JobDescription, WorkArrangement } from '../../types/job-description';

interface JobDescriptionDialogProps {
  role: any;
  contract: any;
  sow?: any;
  profile: any;
  proposalContent?: any;
  /** When present, the dialog recalls this saved JD instead of generating a new one. */
  savedJd?: JobDescription | null;
  /** Persists the current (edited) JD. Enables the Save button when provided. */
  onSave?: (jd: JobDescription) => Promise<void>;
  onClose: () => void;
}

const WORK_ARRANGEMENTS: WorkArrangement[] = ['On-site', 'Remote', 'Hybrid', 'Unspecified'];

export function JobDescriptionDialog({ role, contract, sow, profile, proposalContent, savedJd, onSave, onClose }: JobDescriptionDialogProps) {
  const [jd, setJd] = useState<JobDescription | null>(savedJd || null);
  // Recalled JDs load instantly; only a fresh generate shows the spinner.
  const [loading, setLoading] = useState(!savedJd);
  const [error, setError] = useState<string | null>(null);
  const [salaryRange, setSalaryRange] = useState(savedJd?.salaryRange || '');
  const [contingentOnAward, setContingentOnAward] = useState(savedJd ? savedJd.contingentOnAward !== false : true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = buildJobDescriptionPayload({
        role, contract, sow, profile, proposalContent,
        options: { salaryRange: salaryRange.trim() || null, contingentOnAward },
      });
      // Profile boilerplate (About Us / EEO) is pulled verbatim, never via the AI.
      const result = await generateJobDescription(payload, profileVerbatimFields(profile));
      setJd(result);
      setDirty(true);
      if (result.salaryRange && !salaryRange) setSalaryRange(result.salaryRange);
    } catch (e: any) {
      setError(e?.message || 'Failed to generate job description');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, contract, sow, profile]);

  // Generate on open only when there is no saved JD to recall.
  useEffect(() => {
    if (!savedJd) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = useCallback(async () => {
    if (!jd || !onSave) return;
    setSaving(true);
    try {
      await onSave({ ...jd, salaryRange: salaryRange.trim() || null, contingentOnAward });
      setDirty(false);
      toast.success('Job description saved');
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [jd, onSave, salaryRange, contingentOnAward]);

  // ---- immutable field setters (any edit marks the JD dirty so Save re-enables) ----
  const set = (patch: Partial<JobDescription>) => { setDirty(true); setJd((p) => (p ? { ...p, ...patch } : p)); };
  const setReq = (patch: Partial<JobDescription['requiredQualifications']>) => { setDirty(true);
    setJd((p) => (p ? { ...p, requiredQualifications: { ...p.requiredQualifications, ...patch } } : p)); };
  const setLoc = (patch: Partial<JobDescription['location']>) => { setDirty(true);
    setJd((p) => (p ? { ...p, location: { ...p.location, ...patch } } : p)); };

  const linesToArr = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

  const copyText = async () => {
    if (!jd) return;
    try {
      await navigator.clipboard.writeText(jobDescriptionToPlainText({ ...jd, salaryRange: salaryRange.trim() || null }));
      toast.success('Copied as plain text');
    } catch {
      toast.error('Copy failed');
    }
  };
  const dlMd = () => jd && exportJobDescriptionMarkdown({ ...jd, salaryRange: salaryRange.trim() || null });
  const dlDocx = async () => {
    if (!jd) return;
    try { await exportJobDescriptionWord({ ...jd, salaryRange: salaryRange.trim() || null }); toast.success('Word document downloaded'); }
    catch { toast.error('Word export failed'); }
  };

  const field = 'w-full text-sm rounded-md border border-bw-border dark:border-gray-700 bg-bw-surface dark:bg-gray-900 text-bw-ink dark:text-gray-100 px-2 py-1.5';
  const label = 'text-xs font-semibold uppercase tracking-wide text-bw-muted mb-1 block';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-4">
      <div className="bg-bw-surface dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-bw-border dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-bw-ink dark:text-white">Job Description</h2>
            <p className="text-xs text-bw-muted">{role?.title || role?.role || 'Role'} · {savedJd ? 'saved — edit & re-save or regenerate' : 'editable before export'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 rounded-lg">
            <X className="h-5 w-5 text-bw-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-bw-muted">
              <Loader2 className="h-8 w-8 animate-spin mb-3" />
              <p className="text-sm">Generating job description…</p>
            </div>
          )}

          {!loading && error && (
            <div className="p-4 bg-urgent-bg text-urgent rounded-lg text-sm">
              {error}
              <button onClick={run} className="ml-3 underline">Retry</button>
            </div>
          )}

          {!loading && jd && (
            <>
              {/* Source trace */}
              <div className="flex items-start gap-2 p-3 bg-bw-primary-tint dark:bg-blue-900/20 rounded-lg">
                <ShieldCheck className="h-4 w-4 text-bw-primary mt-0.5 flex-shrink-0" />
                <div className="text-xs text-bw-ink dark:text-gray-200">
                  <span className="font-semibold">Required fields sourced from solicitation</span>
                  {jd.sourceTrace?.solicitationNumber ? ` (${jd.sourceTrace.solicitationNumber})` : ''}:{' '}
                  {(jd.sourceTrace?.requiredFieldsFromSolicitation || []).length
                    ? jd.sourceTrace.requiredFieldsFromSolicitation.join(', ')
                    : 'none flagged — verify Required vs. Preferred below.'}
                </div>
              </div>

              {/* Needs-review flags — gaps routed to the human (acronyms, missing duties) */}
              {(() => {
                const flags: string[] = [];
                if (jd.auditRemoved && jd.auditRemoved.length) flags.push(`Provenance audit removed (not in source): ${jd.auditRemoved.join(', ')}`);
                const acr = detectUnknownAcronyms(jd);
                if (acr.length) flags.push(`Unrecognized acronyms — expand or remove: ${acr.join(', ')}`);
                if (!(jd.keyResponsibilities || []).length) flags.push('No candidate-facing responsibilities found in the source — add them below.');
                if (!(jd.requiredQualifications?.clearanceOrBackground || '').trim()) flags.push('No background/clearance in the source — set it at export if applicable.');
                if (!flags.length) return null;
                return (
                  <div className="flex items-start gap-2 p-3 bg-fit-moderate-bg dark:bg-amber-900/20 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-fit-moderate mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-fit-moderate dark:text-amber-200">
                      <span className="font-semibold">Needs your review:</span>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        {flags.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    </div>
                  </div>
                );
              })()}

              {/* Controls */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Salary range (optional)</label>
                  <input className={field} value={salaryRange} onChange={(e) => { setSalaryRange(e.target.value); setDirty(true); }} placeholder="e.g., $95,000–$120,000" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm text-bw-ink dark:text-gray-200">
                    <input type="checkbox" checked={contingentOnAward} onChange={(e) => { setContingentOnAward(e.target.checked); set({ contingentOnAward: e.target.checked }); setDirty(true); }} />
                    Contingent on award
                  </label>
                </div>
              </div>

              <div>
                <label className={label}>Job title</label>
                <input className={field} value={jd.jobTitle} onChange={(e) => set({ jobTitle: e.target.value })} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>City</label><input className={field} value={jd.location.city} onChange={(e) => setLoc({ city: e.target.value })} /></div>
                <div><label className={label}>State</label><input className={field} value={jd.location.state} onChange={(e) => setLoc({ state: e.target.value })} /></div>
                <div>
                  <label className={label}>Work arrangement</label>
                  <select className={field} value={jd.location.workArrangement} onChange={(e) => setLoc({ workArrangement: e.target.value as WorkArrangement })}>
                    {WORK_ARRANGEMENTS.map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
                <div><label className={label}>Employment type</label><input className={field} value={jd.employmentType} onChange={(e) => set({ employmentType: e.target.value })} /></div>
              </div>

              <div><label className={label}>About us</label><textarea className={field} rows={3} value={jd.companyOverview} onChange={(e) => set({ companyOverview: e.target.value })} /></div>
              <div><label className={label}>Position summary</label><textarea className={field} rows={3} value={jd.positionSummary} onChange={(e) => set({ positionSummary: e.target.value })} /></div>
              <div><label className={label}>Key responsibilities — what this person does (one per line)</label><textarea className={field} rows={6} value={(jd.keyResponsibilities || []).join('\n')} onChange={(e) => set({ keyResponsibilities: linesToArr(e.target.value) })} /></div>
              <div><label className={label}>Program scope — contract tasks in plain language (one per line)</label><textarea className={field} rows={4} value={(jd.programScope || []).join('\n')} onChange={(e) => set({ programScope: linesToArr(e.target.value) })} /></div>

              <div className="rounded-lg border border-bw-border dark:border-gray-700 p-3 space-y-3">
                <p className="text-xs font-bold text-bw-primary uppercase">Required Qualifications</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={label}>Education</label><input className={field} value={jd.requiredQualifications.education} onChange={(e) => setReq({ education: e.target.value })} /></div>
                  <div><label className={label}>Years experience</label><input className={field} value={jd.requiredQualifications.yearsExperience} onChange={(e) => setReq({ yearsExperience: e.target.value })} /></div>
                </div>
                <div><label className={label}>Background / clearance (verbatim)</label><input className={field} value={jd.requiredQualifications.clearanceOrBackground} onChange={(e) => setReq({ clearanceOrBackground: e.target.value })} /></div>
                <div><label className={label}>Required certifications (one per line)</label><textarea className={field} rows={2} value={(jd.requiredQualifications.requiredCertifications || []).join('\n')} onChange={(e) => setReq({ requiredCertifications: linesToArr(e.target.value) })} /></div>
                <div><label className={label}>Required skills (one per line)</label><textarea className={field} rows={4} value={(jd.requiredQualifications.requiredSkills || []).join('\n')} onChange={(e) => setReq({ requiredSkills: linesToArr(e.target.value) })} /></div>
              </div>

              <div><label className={label}>Preferred qualifications (one per line)</label><textarea className={field} rows={4} value={(jd.preferredQualifications || []).join('\n')} onChange={(e) => set({ preferredQualifications: linesToArr(e.target.value) })} /></div>
              <div><label className={label}>Travel</label><input className={field} value={jd.travel} onChange={(e) => set({ travel: e.target.value })} /></div>
              <div><label className={label}>Equal opportunity statement</label><textarea className={field} rows={3} value={jd.eeoStatement} onChange={(e) => set({ eeoStatement: e.target.value })} /></div>
              <div><label className={label}>How to apply</label><textarea className={field} rows={2} value={jd.howToApply} onChange={(e) => set({ howToApply: e.target.value })} /></div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-bw-border dark:border-gray-700 flex flex-wrap gap-2 justify-between">
          <button onClick={run} disabled={loading} className="px-3 py-2 text-sm rounded-lg border border-bw-border dark:border-gray-600 text-bw-ink dark:text-gray-200 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
            <RefreshCw className="h-4 w-4" /> Regenerate
          </button>
          <div className="flex gap-2">
            {onSave && (
              <button onClick={handleSave} disabled={!jd || saving || (!dirty && Boolean(savedJd))} className="px-3 py-2 text-sm rounded-lg border border-bw-primary text-bw-primary hover:bg-bw-primary-tint disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {savedJd && !dirty ? 'Saved' : 'Save'}
              </button>
            )}
            <button onClick={copyText} disabled={!jd} className="px-3 py-2 text-sm rounded-lg border border-bw-border dark:border-gray-600 text-bw-ink dark:text-gray-200 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <Copy className="h-4 w-4" /> Copy
            </button>
            <button onClick={dlMd} disabled={!jd} className="px-3 py-2 text-sm rounded-lg border border-bw-border dark:border-gray-600 text-bw-ink dark:text-gray-200 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5">
              <FileText className="h-4 w-4" /> .md
            </button>
            <button onClick={dlDocx} disabled={!jd} className="px-3 py-2 text-sm rounded-lg bg-bw-primary text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
              <FileDown className="h-4 w-4" /> .docx
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobDescriptionDialog;
